import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Check, Shield, Star, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { LicenseType } from '../types/database';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

export default function Pricing() {
    const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadLicenseTypes();
    }, []);

    const loadLicenseTypes = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('license_types')
                .select('*')
                .eq('is_active', true)
                .order('price', { ascending: true });

            if (!error && data) {
                setLicenseTypes(data);
            } else {
                console.error('Erreur lors du chargement des types de licence:', error);
                // En cas d'erreur, on peut afficher des plans par défaut
                setLicenseTypes([]);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des types de licence:', error);
            setLicenseTypes([]);
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
        }).format(price);
    };

    const handleGetStarted = (licenseTypeId: string) => {
        if (user) {
            navigate('/purchase', { state: { selectedLicenseId: licenseTypeId } });
        } else {
            navigate('/login', { state: { returnTo: '/purchase', selectedLicenseId: licenseTypeId } });
        }
    };

    const getFeaturesList = (features: Record<string, boolean | string | number>) => {
        const defaultFeatures = [
            'Sélection aléatoire de morceaux MP3',
            'Critères de filtrage avancés',
            'Interface moderne et intuitive',
            'Copie automatique vers dossier cible',
            'Gestion des métadonnées MP3'
        ];

        if (features?.premium) {
            return [
                ...defaultFeatures,
                'Support technique prioritaire',
                'Mises à jour gratuites à vie',
                'Fonctionnalités premium exclusives'
            ];
        }

        return defaultFeatures;
    };

    const getPlanIcon = (name: string) => {
        switch (name.toLowerCase()) {
            case 'lifetime':
                return <Star className="h-8 w-8" />;
            case 'premium':
                return <Zap className="h-8 w-8" />;
            default:
                return <Shield className="h-8 w-8" />;
        }
    };

    if (loading) {
        return (
            <section className="py-20 px-4">
                <div className="max-w-6xl mx-auto text-center">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
                        <div className="h-4 bg-gray-300 rounded w-96 mx-auto mb-8"></div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-96 bg-gray-300 rounded-2xl"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto">
                {/* En-tête de section */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        Choisissez votre{' '}
                        <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                            licence
                        </span>
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                        Profitez de toutes les fonctionnalités de MP3 Random Selector avec nos options de licence flexibles.
                    </p>
                </motion.div>

                {/* Grille des tarifs */}
                {licenseTypes.length === 0 ? (
                    // Plan par défaut si aucune licence n'est disponible en base
                    <div className="grid grid-cols-1 max-w-md mx-auto">
                        <div className="relative bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl ring-2 ring-purple-500 shadow-lg">
                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg">
                                    Licence Standard
                                </div>
                            </div>

                            <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white mb-4">
                                    <Star className="h-8 w-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    Licence Complète
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Accès complet à toutes les fonctionnalités
                                </p>
                            </div>

                            <div className="text-center mb-8">
                                <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                                    Prix sur demande
                                </div>
                                <div className="text-gray-600 dark:text-gray-300">
                                    licence à vie
                                </div>
                            </div>

                            <div className="space-y-4 mb-8">
                                {getFeaturesList({}).map((feature, featureIndex) => (
                                    <div key={featureIndex} className="flex items-start space-x-3">
                                        <div className="flex-shrink-0 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mt-0.5">
                                            <Check className="h-3 w-3 text-white" />
                                        </div>
                                        <span className="text-gray-700 dark:text-gray-300 text-sm">
                                            {feature}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => navigate(user ? '/purchase' : '/login')}
                                className="w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl"
                            >
                                {user ? 'Voir les options' : 'Commencer'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={`grid gap-8 ${licenseTypes.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : licenseTypes.length === 2 ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                        {licenseTypes.map((licenseType, index) => {
                            const isPopular = licenseType.name.toLowerCase() === 'lifetime' || index === Math.floor(licenseTypes.length / 2);
                            const features = getFeaturesList(licenseType.features || {});

                            return (
                                <motion.div
                                    key={licenseType.id}
                                    className={`relative bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl ${isPopular ? 'ring-2 ring-purple-500 shadow-lg' : ''
                                        }`}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -5 }}
                                >
                                    {/* Badge populaire */}
                                    {isPopular && (
                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg">
                                                Populaire
                                            </div>
                                        </div>
                                    )}

                                    {/* Icône et titre */}
                                    <div className="text-center mb-8">
                                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white mb-4">
                                            {getPlanIcon(licenseType.name)}
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                            {licenseType.name}
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-300">
                                            {licenseType.description}
                                        </p>
                                    </div>

                                    {/* Prix */}
                                    <div className="text-center mb-8">
                                        <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                                            {formatPrice(licenseType.price)}
                                        </div>
                                        <div className="text-gray-600 dark:text-gray-300">
                                            {licenseType.duration_days ? (
                                                licenseType.duration_days === 365 ? 'par an' : `pour ${licenseType.duration_days} jours`
                                            ) : (
                                                'licence à vie'
                                            )}
                                        </div>
                                    </div>

                                    {/* Fonctionnalités */}
                                    <div className="space-y-4 mb-8">
                                        {features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-start space-x-3">
                                                <div className="flex-shrink-0 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mt-0.5">
                                                    <Check className="h-3 w-3 text-white" />
                                                </div>
                                                <span className="text-gray-700 dark:text-gray-300 text-sm">
                                                    {feature}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Informations sur les appareils */}
                                    {licenseType.max_devices && (
                                        <div className="text-center mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                            <span className="text-sm text-blue-800 dark:text-blue-300">
                                                Jusqu'à {licenseType.max_devices} {licenseType.max_devices === 1 ? 'appareil' : 'appareils'}
                                            </span>
                                        </div>
                                    )}

                                    {/* Bouton d'action */}
                                    <button
                                        onClick={() => handleGetStarted(licenseType.id)}
                                        className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 ${isPopular
                                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl'
                                                : 'bg-white/20 dark:bg-gray-800/50 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 hover:bg-white/30 dark:hover:bg-gray-700/50'
                                            }`}
                                    >
                                        {user ? 'Acheter maintenant' : 'Commencer'}
                                    </button>

                          
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Section d'information supplémentaire */}
                <motion.div
                    className="mt-16 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                >
                    <div className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 rounded-2xl p-8 border border-purple-200 dark:border-purple-800">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                            Garantie de satisfaction
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                            Nous sommes convaincus que vous allez adorer MP3 Random Selector.
                            Si vous n'êtes pas satisfait, contactez-nous dans les 30 jours pour un remboursement complet.
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
